import PropTypes from 'prop-types';
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { MessageConsumer } from '../../../contexts/MessageContext';
import { Consumer as PageConsumer } from '../../../contexts/PageContext';
import { messageTypes } from '../../Includes/variables';
import EmptyMessages from '../EmptyMessages/EmptyMessages';
import Header from '../Header/Header';
import Messages from '../Messages/Messages';
import MessagesLoadingShineComponent from '../MessagesLoadingShineComponent/MessagesLoadingShineComponent';
import './StandaloneMessages.scss';
import { PAGE_DISPLAY_MODE } from '../../../pages/constants';

const defaultLogoSrc = '/v1/avatar_black_160_vtg7lw.svg';

export class StandaloneMessages extends React.PureComponent {
    static propTypes = {
        /**
         * boolean of whether message box is active or not.
         * if it's active, the message box will cover up the entire page, otherwise it will be hidden
         * the styling only effect if the media queries in mobile size
         */
        isMessageBoxActive: PropTypes.bool.isRequired,
        /**
         * channel that user is currently chatting with
         */
        currentChannel: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.number,
        ]),
        /**
         * id of currently logged in user
         */
        currentUserId: PropTypes.string.isRequired,
        /**
         * function that toggles on messages box's active state
         */
        toggleMessageBoxActive: PropTypes.func.isRequired,
        /**
         * function passed in to check if user is the current logged in user
         */
        isLoggedInUser: PropTypes.func.isRequired,
        /**
         * type of messages
         */
        messageType: PropTypes.oneOf(Object.values(messageTypes)),
        /**
         * whether if there are no display channels for groups or not
         */
        emptyGroupMessages: PropTypes.bool,
        /**
         * whether if there are no display channels for Hiring Companies or not
         */
        emptyEmployerMessages: PropTypes.bool,
    };

    static defaultProps = {
        currentChannel: null,
        currentUserId: '',
        isMessageBoxActive: false,
        toggleMessageBoxActive: () => {},
        location: '',
    };

    getHeaderInfo = () => {
        const {
            currentChannel,
            currentUserId,
            toggleMessageBoxActive,
            messageType,
            pageDisplayMode,
        } = this.props;

        switch (messageType) {
            case messageTypes.GROUP:
                const group = currentChannel.channelData.group;
                return {
                    logo: group.logo,
                    name: group.name,
                    hash: group.hash,
                    onGoBack: toggleMessageBoxActive,
                };
            case messageTypes.EMPLOYER:
                if (pageDisplayMode !== PAGE_DISPLAY_MODE.EMPLOYER_DEFAULT) {
                    const {
                        company,
                    } = currentChannel.channelData.recruiterChatInfo;
                    return {
                        logo: company ? company.logo : '/icons/company_2x.png',
                        name: company ? company.companyName : 'Unnamed Company',
                        onGoBack: toggleMessageBoxActive,
                    };
                }
            default:
                const currentChannelTargetUserId = Object.keys(
                    currentChannel.members
                ).filter((member) => member !== currentUserId)[0];
                const currentChannelMeta = currentChannel.members[
                    currentChannelTargetUserId
                ]
                    ? currentChannel.members[currentChannelTargetUserId].user
                    : {};
                let { username, logo, credential } = currentChannelMeta;

                if (messageType === messageTypes.EMPLOYER) {
                    const {
                        channelData: {
                            recruiterChatInfo: { candidate },
                        },
                    } = currentChannel;
                    username =
                        (candidate && candidate.professionalUsername) || null;
                    logo = defaultLogoSrc;
                    credential = null;
                }

                return {
                    logo: logo || defaultLogoSrc,
                    name: username,
                    title: credential,
                    onGoBack: toggleMessageBoxActive,
                };
        }
    };

    async componentDidMount() {
        if (this.props.initGroupChat) {
            this.props.initGroupChat();
        }
    }

    render() {
        const {
            currentChannel,
            isLoggedInUser,
            isMessageBoxActive,
            messageType,
            location,
            toggleMessageBoxActive,
            emptyChannels,
            pageDisplayMode,
            featureFlags,
        } = this.props;

        const defaultLogoSrc = '/v1/avatar_black_160_vtg7lw.svg';

        const messagesVisibilityCss =
            !isMessageBoxActive && location !== 'group-chat-page'
                ? 'component-Messages-StandaloneMessages-hidden'
                : '';

        if (emptyChannels) {
            return (
                <EmptyMessages
                    className={messagesVisibilityCss}
                    featureFlags={featureFlags}
                    messageType={messageType}
                    pageDisplayMode={pageDisplayMode}
                />
            );
        }

        if (currentChannel) {
            return (
                <div
                    className={`
                    component-Messages-StandaloneMessages
                    ${messagesVisibilityCss}
                `}
                >
                    <Header {...this.getHeaderInfo()} type={messageType} />
                    <Messages
                        isLoggedInUser={isLoggedInUser}
                        isMessageBoxActive={isMessageBoxActive}
                        currentChannel={currentChannel}
                    />
                </div>
            );
        } else if (!currentChannel && isMessageBoxActive) {
            toggleMessageBoxActive();
        } else if (!currentChannel && location === 'group-chat-page') {
            if (window) {
                window.location.reload();
            }
        }
        return (
            <div
                className={`
                component-Messages-StandaloneMessages
                ${messagesVisibilityCss}
            `}
            >
                <Header
                    logo={defaultLogoSrc}
                    name={''}
                    type={messageTypes.COMMUNITY}
                />
                <Messages
                    isLoggedInUser={isLoggedInUser}
                    isMessageBoxActive={isMessageBoxActive}
                />
            </div>
        );
    }
}

export default (props) => (
    <PageConsumer>
        {({
            pageProps: {
                featureFlags,
                session: { id },
            },
            pageDisplayMode,
        }) => (
            <MessageConsumer>
                {({
                    getCurrentChannelInfo,
                    isLoggedInUser,
                    initialLoad,
                    getChannelInfoByKey,
                    getSelectedType,
                    getDisplayChannels,
                    initGroupChat,
                }) => {
                    const { channelKey, messageType } = props;
                    const currentChannelInfo =
                        channelKey && messageType
                            ? getChannelInfoByKey(channelKey, messageType)
                            : getCurrentChannelInfo();
                    const emptyChannels =
                        !initialLoad && getDisplayChannels().length === 0;

                    // display loading shine while chat client is initializing
                    if (initialLoad) return <MessagesLoadingShineComponent />;

                    return (
                        <BrowserRouter>
                            <StandaloneMessages
                                {...props}
                                currentChannel={currentChannelInfo}
                                currentUserId={id.toString()}
                                isLoggedInUser={isLoggedInUser}
                                messageType={messageType || getSelectedType()}
                                emptyChannels={emptyChannels}
                                initGroupChat={initGroupChat}
                                featureFlags={featureFlags}
                                pageDisplayMode={pageDisplayMode}
                            />
                        </BrowserRouter>
                    );
                }}
            </MessageConsumer>
        )}
    </PageConsumer>
);
